import { Grid, Theme, Typography, useTheme, Button, styled } from "@mui/material";
import {
  decodePairingDefinition,
  getDefaultPlannedCreditMap,
  getDefinitionsFromCSV,
  IPairingBuilderDefinition,
  IPairingBuilderDefinitionPositions,
  IPlannedCreditMap,
} from "@vmosolutions/oc-interfaces";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FileUpload } from "src/components/FileUpload/FileUpload";
import { CheckboxField } from "src/components/FormikComponents/CheckboxField";
import { DateTimeField } from "src/components/FormikComponents/DateTimeField";
import { closeModal, openModal } from "src/redux/actions/App.Actions";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { ab82str, isArrayBuffer } from "src/utils/index";
import { PairingDefinitionBuilder } from "./PairingDefinitionBuilder";
import Tooltip from "@mui/material/Tooltip";

interface IPairingBuilderWindowProps {
  children: any;
}

const defaultDefinition: IPairingBuilderDefinition = {
  name: undefined,
  recurrenceSimple: {
    daysOfWeek: [], // days of week
    startDayOfMonth: undefined, // start day of month
    endDayOfMonth: undefined, // end day of month
  },
  raw: "",
  checkInOffset: undefined, // minutes
  plannedCredit: undefined, // a value that just gets set on pairing build. it maps to `pairing.plannedCredit`
  plannedCreditMap: undefined,
  absStart: undefined, // an absolute day that this starts. local based on the base location of this pairing
  absEnd: undefined, // an absolute date we stop looking for this pairing. (based on the start of the pairing)
  events: [], // ordered events array
  aircraftType: undefined, // aircraft type object id (maybe not a thing)
  positions: [],
  maxPairingHours: undefined, // the maximum number of hours this pairing will build for
  baseICAO: undefined, // base of pairing
  airlineCode: undefined,
};

const OCPairingBuilderWindow = styled(Grid)(({ theme }) => ({
  width: "100%",
  padding: 15,
  "& .pairing_builder_header": {
    backgroundColor: theme.custom.timeline.contextListBackground,
    padding: 10,
    borderRadius: 5,
    "& .file_upload": {
      width: "100%",
      marginBottom: 10,
    },
    "& .pattern_match_daterange": {
      width: "auto",
      margin: "5px 10px",
      flexWrap: "wrap",
      rowGap: 8,
      columnGap: 12,
      // Keep each top-level group at its natural width so labels and inputs
      // don't compress into each other as the window narrows; the group wraps
      // to a new row instead.
      "& > *": {
        flexShrink: 0,
      },
      "& .prefix": {
        fontSize: 12,
        lineHeight: "14px",
      },
      "& .datetimefield": {
        marginLeft: 0,
        "& label": { width: "auto" },
      },
    },
    "& .header_buttons": {
      width: "auto",
      marginRight: 0,
      marginLeft: "auto",
      "& .add_new_definition_btn, & .remove_all_definitions_btn": {
        fontSize: 12,
        margin: "0px 5px",
      },
    },
  },
  "& .pairing_definition": {
    margin: "1px 0px",
    borderRadius: 5,
    opacity: 0.5,
    padding: 5,
    "&.active": {
      backgroundColor: theme.custom.timeline.contextListBackground,
      margin: "10px 0px",
      opacity: 1,
    },
    "&.dirty": {
      "& .edit_mode_button": {
        border: `1px solid ${theme.custom.forms.dirtyColor}`,
        "& svg": {
          color: theme.custom.forms.dirtyColor,
        },
      },
    },
    "& .edit_mode_button": {
      padding: 0,
      margin: "2px 9px",
      border: `1px solid ${theme.custom.forms.pristineColor}`,
      borderRadius: 3,
      width: 20,
      height: 20,
      "& svg": {
        color: theme.custom.forms.pristineColor,
        width: 14,
        height: 14,
      },
    },
    "&.header": {
      margin: "10px 0px 0px 0px",
      opacity: 1,
    },
    "& .definition_index": {
      width: 20,
      textAlign: "right",
      marginRight: 5,
      marginTop: 4,
    },
    "& .dirty": {
      "& .definition_name": {
        color: theme.custom.forms.dirtyColor,
      },
    },
    // Styled to match Field labels
    "& .definition_name": {
      width: 80,
      color: theme.custom.forms.labels,
      marginRight: 10,
      position: "relative",
      fontSize: 12,
      textAlign: "right",
      lineHeight: "12px",
    },
    "& .definition_string_control": {
      padding: 0,
      margin: 5,
      width: 440,
    },
    "& .positions_control": {
      width: "auto",
      height: 24,
      margin: 5,
      "& .position_slot": {
        width: 35,
      },
    },
    "& .weekdays_control": {
      width: 230,
    },
    "& .logicmodifiers_control": {
      width: "auto",
      minWidth: 300,
    },
    "& .checkinoffset_control": {
      width: 100,
      whiteSpace: "nowrap",
    },
    "& .checkinoffset_control_checkbox": {
      width: 50,
      whiteSpace: "nowrap",
      marginRight: 20,
    },
    "& .start_control, .end_control": {
      overflow: "none",
      width: "auto",
      "& .react-select__control": { width: 75, minWidth: 75 },
    },
    "& .plannedcredit_control": {
      width: 260,
    },
    "& .controls": {
      display: "none",
      "&.show": { display: "flex" },
    },
    "& .actions": {
      marginLeft: 20,
      "& button": {
        fontSize: 12,
        margin: "10px 0px 5px 10px",
      },
    },
    "& .add_remove_buttons": {
      width: "auto",
      marginLeft: "auto",
      marginRight: 25,
      "& button.add_definition_btn, & button.remove_definition_btn": { margin: 0 },
    },
  },
}));

const randomDefinitionName = (): string => {
  let result = "";
  const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  const charactersLength = characters.length;
  for (let i = 0; i < 5; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }
  return `definition_${result}`;
};

/**
 * Remove incompatible patterns from available list
 * TODO consider moving this function into a backend api call if it requires more complex sanity checks.
 * @param patterns - pairing creation pattern
 */
const cleanOutPatterns = (patterns: string[]): string[] => {
  const validPatterns: string[] = [];
  patterns.forEach((pattern) => {
    if (typeof pattern === "string" && pattern.length > 0) {
      //&& /\d/.test(pattern
      validPatterns.push(pattern);
    }
  });
  return validPatterns;
};

export const PairingBuilderWindow = (props: IPairingBuilderWindowProps): JSX.Element => {
  // Set pending position flight.
  const theme: Theme = useTheme();
  const dispatch = useDispatch();
  const fileUploadRef = useRef<any>(null);

  // Will force a refresh of component so that FileUpload is updated correctly.
  const [iteration, setIteration] = useState<number>(0);
  const [pairingPatterns, setPairingPatterns] = useState<string[]>([]);
  const [pairingDefinitions, setPairingDefinitions] = useState<IPairingBuilderDefinition[]>([]);
  const [activeDef, setActiveDef] = useState<number>(0);

  const [searchStart, setSearchStart] = useState<number>(new Date()?.valueOf()); //jan 1st the start and end should be something the user selects.
  const [searchEnd, setSearchEnd] = useState<number>(null);
  const [includeDuplicates, setIncludeDuplicates] = useState<boolean>(false); //if true, match to flights without check for crew spots on the aircraft
  const [includeUnassignedFlights, setIncludeUnassignedFlights] = useState<boolean>(false); //if true, match to flights that are both assigned or unassigned, otherwise just assigned

  const customerStructure = useSelector((state: IRootState) => state.structures.customerStructure);
  const { positions, nonFlyingTypes, globalAppConfig } = customerStructure;
  const airlineCode = globalAppConfig.customer.iataCode;

  const creditSettingValue = useSelector((state: IRootState) => state.credit);
  const defaultPlannedCreditMap = useMemo<IPlannedCreditMap>(() => {
    if (creditSettingValue?.settings && creditSettingValue.settings.length > 0) {
      const defaultMap = getDefaultPlannedCreditMap(creditSettingValue.settings);
      return defaultMap;
    }
    return undefined;
  }, [creditSettingValue]);
  // Map out initial positions based on available customer positions.
  const initialPositionsValue: IPairingBuilderDefinitionPositions[] = useMemo(
    () => positions.map((pos) => ({ position: pos.title, count: 0 })),
    [positions]
  );

  /**
   * Update the form with the current file info and contents.
   * @param _newFile -
   * @param _fileContents -
   */
  const gotFile = (_newFile: any, _fileContents: ArrayBuffer): void => {
    if (_newFile.type === "application/vnd.ms-excel" || _newFile.type === ".csv" || _newFile.type === "text/csv") {
      const patternArray = !!_fileContents && isArrayBuffer(_fileContents) ? ab82str(_fileContents).split(/\n/) : [];
      setPairingPatterns(cleanOutPatterns(patternArray));
    } else {
      setPairingPatterns([]);
    }
  };

  /** Remove the imported file and clear the drop tool */
  const handleRemoveFile = useCallback((): void => {
    setIteration((prev) => prev + 1);
    setPairingPatterns([]);
  }, []);

  /**
   * Function will be called anytime changes are made to pairingPatterns.
   */
  // const getDefs = useCallback(async (patterns: string[]): Promise<IPairingBuilderDefinition[]> => {
  //   const results: IPairingBuilderDefinition[] = await AmplifyAPIWrapper.Post<any>("/pairingbuilder", patterns, {});
  //   return Array.isArray(results) ? results : [];
  // }, []);

  useEffect(() => {
    if (pairingPatterns.length > 0 && nonFlyingTypes.length > 0 && positions.length > 0) {
      const results = getDefinitionsFromCSV(
        pairingPatterns,
        positions,
        nonFlyingTypes,
        airlineCode,
        defaultPlannedCreditMap
      );
      if (results && Array.isArray(results)) {
        // Spread new results into existing definitions.
        setPairingDefinitions((prev) => [...prev, ...results]); // set definitions returned from backend.
        handleRemoveFile(); // clear file drop.
      }
    }
  }, [airlineCode, nonFlyingTypes, pairingPatterns, positions, handleRemoveFile, defaultPlannedCreditMap]);

  // Add new definition at selected index and make active
  const handleAddNewDefinition = useCallback(
    (addAtIndex: number) => {
      setPairingDefinitions((prev) => {
        const newEntry = {
          ...defaultDefinition,
          name: randomDefinitionName(),
          positions: initialPositionsValue,
          plannedCreditMap: defaultPlannedCreditMap,
        };
        prev.splice(addAtIndex, 0, newEntry);
        return [...prev]; // return shallow copy with item added
      });
      setActiveDef(addAtIndex);
    },
    [defaultPlannedCreditMap, initialPositionsValue]
  );

  // Delete selected definition and change actively selected to previous.
  const handleRemoveDefinition = useCallback(
    (removeIndex: number, name: string) => {
      dispatch(
        openModal({
          root: {
            data: {
              title: `Remove "${name}"?`,
              message: "All changes in this definition will be lost if removed.",
              buttons: [
                {
                  label: "CANCEL",
                  handler: () => {
                    dispatch(closeModal({ root: { data: null } }));
                  },
                },
                {
                  label: "REMOVE DEFINITION",
                  handler: () => {
                    setPairingDefinitions((prev) => {
                      prev.splice(removeIndex, 1);
                      return [...prev]; // return shallow copy with item removed
                    });
                    setActiveDef((prev) => (prev > 0 ? prev - 1 : 0));
                    dispatch(closeModal({ root: { data: null } }));
                  },
                },
              ],
            },
          },
        })
      );
    },
    [dispatch]
  );

  const handleClearAllDefintions = useCallback(() => {
    dispatch(
      openModal({
        root: {
          data: {
            title: "Remove All Definitions?",
            message: "Any changes made will be lost if removed.",
            buttons: [
              {
                label: "CANCEL",
                handler: () => {
                  dispatch(closeModal({ root: { data: null } }));
                },
              },
              {
                label: "REMOVE DEFINITIONS",
                handler: () => {
                  setPairingDefinitions([]);
                  setActiveDef(0);
                  dispatch(closeModal({ root: { data: null } }));
                },
              },
            ],
          },
        },
      })
    );
  }, [dispatch]);

  // If user modifies one of the assigned pairing strings, update the pairing pattern array.
  //TODO At the moment, any change to any of the pattern strings will trigger a retrieval of all of them. Look into improving this by only retrieving the required one.
  const updateDefinition = useCallback(
    (newPattern: IPairingBuilderDefinition, index: number): IPairingBuilderDefinition => {
      const updatedDef = decodePairingDefinition(
        {
          name: newPattern.name,
          daysOfWeek: newPattern.recurrenceSimple?.daysOfWeek?.join(""),
          definition: newPattern.raw,
          checkInOffset: newPattern.checkInOffset,
          plannedCredit: newPattern.plannedCredit,
          plannedCreditMap: newPattern.plannedCreditMap,
          startDayOfMonth: newPattern.recurrenceSimple?.startDayOfMonth,
          endDayOfMonth: newPattern.recurrenceSimple?.endDayOfMonth,
          absStart: newPattern.absStart,
          absEnd: newPattern.absEnd,
          positions: newPattern.positions,
          isBiddable: newPattern.isBiddable,
          includeSplits: newPattern.includeSplits,
          logicModifiers: newPattern.logicModifiers,
        },
        nonFlyingTypes,
        airlineCode
      );

      //now update the defintion - this will update the child compnent
      setPairingDefinitions((prev) => {
        const updatedPairingDefinitions = [...prev];
        updatedPairingDefinitions[index] = updatedDef;

        return updatedPairingDefinitions;
      });

      // Returned so the child can hand the decoded definition directly to the
      // backend in the same click — avoids waiting for the props.definition
      // round-trip before View/Build can fire.
      return updatedDef;
    },
    [airlineCode, nonFlyingTypes]
  );

  // const patternColumns = pairingPatterns?.[0] ? pairingPatterns[0].split(",") : [];
  // const definitionIndex = patternColumns.findIndex((label) => label === "Definition");
  // useEffect(() => { console.log(includeDuplicates) }, [includeDuplicates]);

  return (
    <OCPairingBuilderWindow item key={`builder_${iteration}`}>
      <Grid container direction="column">
        <Grid container direction="column" className="pairing_builder_header">
          <Grid item className="file_upload">
            <FileUpload
              ref={fileUploadRef}
              onFile={gotFile}
              handleRemoveFile={handleRemoveFile}
              dropMessage={
                pairingPatterns?.length
                  ? ""
                  : "To add build definitions, drag (or click to select) an CSV file containing valid pairing flight patterns. Alternatively, you create a definition by clicking the button below."
              }
              dropZoneInputProps={{ accept: ".csv" }}
            />
          </Grid>
          <Grid container className="pattern_match_daterange" alignItems="center">
            <Typography variant="caption">Pattern Match&nbsp;&nbsp;&nbsp;—</Typography>
            <Grid item>
              <DateTimeField
                className="datetimefield"
                name="pattern_start"
                label="Start"
                value={searchStart}
                onChange={(value) => setSearchStart(value as number)}
                isEpoch={true}
                maxDate={searchEnd}
                // minDate={new Date()?.valueOf()}
                // maxDate={new Date()}
                hideTimeControl={true}
              />
            </Grid>
            <Grid item>
              <DateTimeField
                className="datetimefield"
                name="pattern_end"
                label="End"
                value={searchEnd}
                onChange={(value) => setSearchEnd(value as number)}
                isEpoch={true}
                minDate={searchStart}
                // maxDate={new Date()}
                hideTimeControl={true}
              />
            </Grid>
            <Tooltip
              title="Match results include flights that already have pairings covering matching crew positions"
              placement="top"
            >
              <Grid item>
                <CheckboxField
                  className="checkfield includeDuplicates"
                  name="includeDuplicates"
                  label="Include Duplicates"
                  value={includeDuplicates}
                  onChange={() => setIncludeDuplicates(!includeDuplicates)}
                />
              </Grid>
            </Tooltip>
            <Tooltip title="Match results include unassigned flights" placement="top">
              <Grid item>
                <CheckboxField
                  className="checkfield includeUnassignedFlights"
                  name="includeUnassignedFlights"
                  label="Include Unassigned Flights"
                  value={includeUnassignedFlights}
                  onChange={() => setIncludeUnassignedFlights(!includeUnassignedFlights)}
                />
              </Grid>
            </Tooltip>
            <Grid container className="header_buttons">
              <Button
                className="add_new_definition_btn"
                variant="outlined"
                onClick={() => {
                  // Add a new definition at the end of the current list.
                  handleAddNewDefinition(pairingDefinitions?.length);
                }}
              >
                Add New Definition
              </Button>
              <Button
                className="remove_all_definitions_btn"
                variant="outlined"
                onClick={() => handleClearAllDefintions()}
              >
                Remove All Definitions
              </Button>
            </Grid>
          </Grid>
        </Grid>

        {pairingDefinitions.map((definition, index) => {
          const definitionIndex = index;
          // The random named assigned to the newly created definition is important as it forced the pairing builder to remount/reset
          return (
            <Grid item key={`${definition?.name}_${index}`}>
              <PairingDefinitionBuilder
                definition={definition}
                updateDefinition={updateDefinition}
                index={definitionIndex}
                setActiveDefinition={setActiveDef}
                active={activeDef === definitionIndex}
                start={searchStart}
                end={searchEnd}
                includeDuplicates={includeDuplicates}
                includeUnassignedFlights={includeUnassignedFlights}
                // New controls to add/remove definitions
                AddNewDefinition={handleAddNewDefinition}
                removeDefinition={handleRemoveDefinition}
                creditSettingValue={creditSettingValue}
              />
            </Grid>
          );
        })}
      </Grid>
    </OCPairingBuilderWindow>
  );
};
